import { useMutation, useQuery } from "@tanstack/react-query";

import {
  addCollaboratorToCourse,
  addCourse,
  deleteCourse,
  getCourse,
  getCourseModules,
  getCourses,
  publishCourse,
  removeCollaboratorFromCourse,
  updateCourse,
} from "@/api";
import type { Course } from "@/types";

export const coursesQueryKeys = {
  useCourses: () => ["courses", "getCourses"],
  useCourse: (courseId?: Course["id"]) => ["courses", "getCourse", courseId],
  useCourseModules: (courseId?: Course["id"]) => [
    "courses",
    "getCourseModules",
    courseId,
  ],
};

export const useCourses = () => {
  const useAllCourses = () =>
    useQuery({
      queryFn: () => getCourses(),
      queryKey: coursesQueryKeys.useCourses(),
    });

  const useCourse = (courseId?: Course["id"]) =>
    useQuery({
      queryFn: () => getCourse(courseId),
      queryKey: coursesQueryKeys.useCourse(courseId),
      enabled: !!courseId,
    });

  const useCourseModules = (courseId?: Course["id"]) =>
    useQuery({
      queryFn: () => getCourseModules(courseId),
      queryKey: coursesQueryKeys.useCourseModules(courseId),
      enabled: !!courseId,
    });

  const useAddCourse = () => useMutation({ mutationFn: addCourse });
  const useUpdateCourse = () => useMutation({ mutationFn: updateCourse });
  const useDeleteCourse = () => useMutation({ mutationFn: deleteCourse });
  const usePublishCourse = () => useMutation({ mutationFn: publishCourse });
  const useAddCollaboratorToCourse = () =>
    useMutation({ mutationFn: addCollaboratorToCourse });
  const useRemoveCollaboratorFromCourse = () =>
    useMutation({ mutationFn: removeCollaboratorFromCourse });

  return {
    useAllCourses,
    useCourse,
    useCourseModules,
    useAddCourse,
    useUpdateCourse,
    useDeleteCourse,
    usePublishCourse,
    useAddCollaboratorToCourse,
    useRemoveCollaboratorFromCourse,
  };
};
